import { FeatureLink } from "~/hec/components/molecules/feature-link/feature-link";
import { useFeatureLinkTarget } from "~/hec/hooks/use-feature-link-target";
import {
  builderImageSrcSet,
  builderImageUrl,
  builderImageWidths,
} from "~/hec/utils/builder-image-url";
import { cn } from "~/lib/utils";

import type { FeatureCardProps, FeatureCardsProps } from "./types";

// Breakpoints are Builder's (small ≤639, medium ≤991), not Tailwind's, so the
// component lays out exactly like the symbol it replaces.
const DARK_ARROW =
  "https://cdn.builder.io/api/v1/image/assets%2F1e07bd57fd054601b600816e06494849%2F1b93b7d1298143c3b097fc9ceee7645e";
// Mobile rows go full, half + half, full, then halves (0-based indexes).
const FULL_WIDTH_ON_MOBILE = new Set([0, 3]);
const WHITE_ARROW =
  "https://cdn.builder.io/api/v1/image/assets%2F1e07bd57fd054601b600816e06494849%2Fb4609ebfa90546d08e8a8becb18db0c6";

// Widest a card gets: ~474px at the 1440px cap, ~390px on mobile (full width card).
const DESKTOP_WIDTHS = builderImageWidths(470);
const MOBILE_WIDTHS = builderImageWidths(390);
const LOGO_WIDTHS = builderImageWidths(150);

/** One card: the whole card is a link when it has a destination. */
function FeatureCard({ item, index }: FeatureCardProps) {
  const link = useFeatureLinkTarget(item.link);
  const image = item.desktopImage || item.mobileImage;
  const fullWidth = FULL_WIDTH_ON_MOBILE.has(index);
  // Image width on mobile, for `sizes`: full row or half a row less the gap.
  const mobileSizes = fullWidth ? "calc(100vw - 16px)" : "calc(50vw - 13px)";

  return (
    <FeatureLink
      target={link}
      // Mobile grid: cards 1 and 4 are full width, the rest half.
      // From 640 it's flex, so col-span no longer applies.
      className={cn(
        "min-[640px]:basis-[calc((100%_-_20px)/3_-_0.5px)] relative flex w-full flex-col items-stretch overflow-hidden rounded-[12px]",
        fullWidth && "col-span-2",
      )}
      data-testid={`feature-cards-card-${index}`}
    >
      {image ? (
        <picture>
          {item.mobileImage ? (
            <source
              media="(max-width: 639px)"
              sizes={mobileSizes}
              srcSet={
                builderImageSrcSet(item.mobileImage, MOBILE_WIDTHS) ??
                item.mobileImage
              }
            />
          ) : null}
          <img
            src={builderImageUrl(image, { width: 940, format: "webp" })}
            srcSet={builderImageSrcSet(image, DESKTOP_WIDTHS)}
            sizes={`(max-width: 639px) ${mobileSizes}, (max-width: 1455px) calc((100vw - 36px) / 3), 474px`}
            alt=""
            loading="lazy"
            decoding="async"
            className="min-[640px]:aspect-[400/253] min-[640px]:min-h-0 block aspect-[1800/885] min-h-[160px] w-full object-cover"
          />
        </picture>
      ) : null}
      <div
        className="min-[640px]:-mt-px min-[640px]:justify-between min-[992px]:justify-start min-[992px]:p-[24px] relative flex w-full grow flex-col items-stretch gap-[12px] p-[18px]"
        style={{ backgroundColor: item.blockColor || undefined }}
      >
        {item.logo ? (
          <img
            src={builderImageUrl(item.logo, { width: 300, format: "webp" })}
            srcSet={builderImageSrcSet(item.logo, LOGO_WIDTHS)}
            sizes="150px"
            alt={item.logoAlt ?? ""}
            loading="lazy"
            decoding="async"
            className="h-[37px] w-full object-contain object-left"
          />
        ) : null}
        <div className="min-[992px]:items-center flex flex-row items-end gap-[6px]">
          {item.headline ? (
            <p
              className="font-grotesque text-[1.125rem] font-light leading-[1.5] text-[rgba(25,25,28,1)]"
              style={{ color: item.textColor || undefined }}
            >
              {item.headline}
            </p>
          ) : null}
          <img
            src={item.whiteArrow ? WHITE_ARROW : DARK_ARROW}
            alt=""
            aria-hidden="true"
            className="mb-[4px] ml-auto w-full max-w-[21px]"
          />
        </div>
      </div>
      {item.badge ? (
        <div
          className="absolute left-0 top-0 flex w-fit flex-col overflow-hidden rounded-[12px_0_12px_0] bg-[#DC0033]"
          data-testid={`feature-cards-badge-${index}`}
        >
          <span className="min-[640px]:text-base min-[992px]:mx-[12px] min-[992px]:my-[15px] mx-[8px] my-[10px] font-grotesque text-[12px] leading-[1.5] text-white">
            {item.badge}
          </span>
        </div>
      ) : null}
    </FeatureLink>
  );
}

/**
 * Organism: the "Telehealth Feature" symbol (COM-945) as a component. A heading
 * over linked cards (photo, logo, headline, optional corner badge) and a
 * button below. On mobile the first card is full width and the rest sit two
 * per row; from 640px three per row, and a 4th card wraps onto a centered row.
 */
export function FeatureCards({
  heading,
  items,
  buttonText,
  buttonLink,
  className,
  ...rest
}: FeatureCardsProps) {
  const button = useFeatureLinkTarget(buttonLink);

  return (
    <section
      className={cn(
        "min-[640px]:mb-[32px] min-[640px]:py-[28px] relative mx-auto mb-[24px] flex min-h-[100px] w-[calc(100%_-_16px)] max-w-[1440px] flex-col py-[18px]",
        className,
      )}
      data-testid="feature-cards-section"
      {...rest}
    >
      {heading ? (
        <h2 className="min-[640px]:mb-[15px] min-[640px]:pb-[0px] min-[640px]:text-[26px] min-[992px]:mb-[20px] min-[992px]:text-[36px] mx-auto mb-[0px] text-balance pb-[10px] text-center font-grotesque text-[26px] font-medium leading-[1.2] text-emphasis">
          {heading}
        </h2>
      ) : null}
      <div className="min-[640px]:flex min-[640px]:flex-row min-[640px]:flex-wrap min-[640px]:justify-center grid grid-cols-2 gap-[10px]">
        {items.map((item, index) => (
          <FeatureCard key={index} item={item} index={index} />
        ))}
      </div>
      {buttonText ? (
        <FeatureLink
          target={button}
          className={cn(
            // Same look as the symbol's primary Core:Button.
            "min-[640px]:text-[12px] min-[992px]:text-[1.125rem] mx-auto mt-[28px] inline-flex w-auto items-center justify-center rounded-[0.5rem] bg-primary px-[28px] py-[20px] font-grotesque text-base font-medium leading-[1.5] text-primary-foreground transition-all hover:bg-primary-hover",
          )}
          data-testid="feature-cards-button"
        >
          {buttonText}
        </FeatureLink>
      ) : null}
    </section>
  );
}
